import React, { useEffect } from 'react';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { useParams } from '@thd-olt-component-react/router';
import { Metadata, NoIndexNoFollowMetadata } from '@thd-olt-component-react/metadata';
import {
  extend,
  params,
  shape,
  arrayOf,
  string as stringType,
  useDataModel,
  QueryContext,
} from '@thd-nucleus/data-sources';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import {
  AuthorBlog,
  AuthorHero,
  AuthorIntroduction,
  AuthorName,
  AuthorTitle,
  ArticleTilesContainer,
} from '@thd-olt-component-react/author-pod';
import {
  Facebook,
  Instagram,
  Pinterest,
  Twitter,
} from '@thd-olt-component-react/social-media-links';
import { declareContexts } from '@thd-olt-component-react/hydrator';
import classNames from 'classnames';

export const Bio = () => {
  declareContexts([ExperienceContext, QueryContext]);
  const { articleGUID } = useParams();

  const { data, loading, error } = useDataModel('article', {
    variables: {
      articleGUID,
    }
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (data && !loading) {
        window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('author-bio-experience.page-load', {
          articleGUID,
        });
      }
    }
  }, []);

  if (!data || loading || error) {
    return null;
  }

  let crumbs = data?.article?.breadcrumbs || [];
  const metadata = data?.article?.metadata || {};
  const authorName = data?.article?.contributorDetails?.contributorName || '';
  crumbs = [...crumbs, { url: '', label: authorName }];

  const {
    facebookProfile,
    pinterestProfile,
    instagramProfile,
    twitterProfile
  } = data?.article?.socialMediaLinks || {};

  const facebookProps = { href: facebookProfile };
  const pinterestProps = { href: pinterestProfile };
  const twitterProps = { href: twitterProfile };
  const instagramProps = { href: instagramProfile };

  return (
    <div
      className={classNames(
        'sui-flex sui-flex-col sui-bg-primary sui-gap-6 sui-px-4 sui-py-4',
        'md:sui-px-6 lg:sui-px-8 xl:sui-px-auto'
      )}
    >
      {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
      <div className="sui-flex sui-flex-col sui-w-full sui-max-w-[1376px] xl:sui-self-center sui-gap-6">
        <ErrorBoundary name="metadata">
          <Metadata data={metadata}>
            <NoIndexNoFollowMetadata />
          </Metadata>
        </ErrorBoundary>

        <div
          className="sui-flex sui-flex-col sui-gap-4"
        >
          <div
            data-testid="author-bio__breadcrumb-wrapper"
            className="author-bio__breadcrumb"
          >
            <ErrorBoundary name="breadcrumbs">
              <Breadcrumbs staticCrumbs={crumbs} />
            </ErrorBoundary>
          </div>

          <div
            data-testid="author-bio__blog-wrapper"
            className={classNames(
              'sui-grid sui-grid-cols-1 sui-grid-rows-1 sui-gap-2',
              'md:sui-grid-cols-8 md:sui-gap-5'
            )}
          >
            <div className="md:sui-col-span-3 xl:sui-col-span-2">
              <ErrorBoundary name="author-hero">
                <AuthorHero articleGUID={articleGUID} />
              </ErrorBoundary>
            </div>

            <div className="md:sui-col-span-5 xl:sui-col-span-6">
              <div className="sui-flex sui-flex-col sui-gap-6">
                <div className="sui-flex sui-flex-col">
                  <div>
                    <ErrorBoundary name="author-name">
                      <AuthorName articleGUID={articleGUID} />
                    </ErrorBoundary>
                  </div>
                  <div>
                    <ErrorBoundary name="author-blog">
                      <AuthorBlog articleGUID={articleGUID} />
                    </ErrorBoundary>
                  </div>
                  <div className="sui-flex sui-flex-row sui-gap-2">
                    <ErrorBoundary name="social">
                      <Facebook props={facebookProps} size="lg" />
                      <Pinterest props={pinterestProps} size="lg" />
                      <Instagram props={instagramProps} size="lg" />
                      <Twitter props={twitterProps} size="lg" />
                    </ErrorBoundary>
                  </div>
                </div>

                <ErrorBoundary name="author-intro">
                  <AuthorIntroduction articleGUID={articleGUID} />
                </ErrorBoundary>
              </div>
            </div>
          </div>
        </div>

        <div
          data-testid="author-bio__tiles-wrapper"
          className="sui-py-3"
        >
          <ErrorBoundary name="tiles-container">
            <ArticleTilesContainer articleGUID={articleGUID} />
          </ErrorBoundary>
        </div>
      </div>
    </div>
  );
};

Bio.propTypes = {};

Bio.dataModel = extend(
  {
    article: params({ articleGUID: stringType() }).shape({
      breadcrumbs: arrayOf(
        shape({
          dimensionName: stringType(),
          url: stringType(),
          label: stringType(),
        })
      ),
      contributorDetails: shape({
        contributorName: stringType(),
      }),
      title: stringType(),
      socialMediaLinks: shape({
        twitterProfile: stringType(),
        facebookProfile: stringType(),
        instagramProfile: stringType(),
        pinterestProfile: stringType(),
      }),
      metadata: shape({
        description: stringType(),
        siteName: stringType(),
        title: stringType(),
        type: stringType(),
        canonicalURL: stringType(),
        facebookAdmins: stringType(),
      }),
    }),
  },
  Breadcrumbs,
  AuthorTitle,
  AuthorHero,
  AuthorName,
  AuthorBlog,
  AuthorIntroduction,
  ArticleTilesContainer
);

Bio.displayName = 'Author Bio';

Bio.defaultProps = {};
